import { isEmpty } from 'lodash/fp'
import * as v from 'valibot'

import { Api } from '@/api/client'
import {
  LearnpathCategories,
  ManagedLearnpath,
  ManagedLearnpathCandidates,
  ManagedLearnpathDuplicateActionResponse,
  ManagedLearnpathDuplicateAsCourseActionResponse,
  ManagedLearnpathDuplicateAsLearnpathActionResponse,
  ManagedLearnpathElementOneOf,
  ManagedLearnpathElements,
  ManagedLearnpathElementsPutRequest,
  ManagedLearnpathEmbeddedVideoElement,
  ManagedLearnpathEmbeddedVideoElementPutRequest,
  ManagedLearnpathEmbeddedVideoElementsPostRequest,
  ManagedLearnpathEventElement,
  ManagedLearnpathEventElementPutRequest,
  ManagedLearnpathEventElementsPostRequest,
  ManagedLearnpathFileElement,
  ManagedLearnpathFileElementPutRequest,
  ManagedLearnpathFileElementsPostRequest,
  ManagedLearnpathLinkElement,
  ManagedLearnpathLinkElementPutRequest,
  ManagedLearnpathLinkElementsPostRequest,
  ManagedLearnpathMembers,
  ManagedLearnpathMembersDeleteRequest,
  ManagedLearnpathMembersPostRequest,
  ManagedLearnpathPlatformTopicElement,
  ManagedLearnpathPlatformTopicElementPostRequest,
  ManagedLearnpathPlatformTopicElementPutRequest,
  ManagedLearnpathPlatformTopicElementsPostResponse,
  ManagedLearnpathPlatformTopicsCatalog,
  ManagedLearnpathPutRequest,
  ManagedLearnpathQuizElement,
  ManagedLearnpathQuizElementPutRequest,
  ManagedLearnpathQuizElementsPostRequest,
  ManagedLearnpathScormElement,
  ManagedLearnpathScormElementPutRequest,
  ManagedLearnpathScormElementsPostRequest,
  ManagedLearnpathSharingLink,
  ManagedLearnpathTextElement,
  ManagedLearnpathTextElementPutRequest,
  ManagedLearnpathTextElementsPostRequest,
  ManagedLearnpathUserGroups,
  ManagedLearnpathUserGroupsDeleteRequest,
  ManagedLearnpathUserGroupsPostRequest,
  ManagedLearnpathUserGroupsPutRequest,
  ManagedLearnpathVideoElement,
  ManagedLearnpathVideoElementPutRequest,
  ManagedLearnpathVideoElementsPostRequest,
  ManagedLearnpaths,
  ManagedLearnpathsPostRequest,
} from '@/api/generated-api-and-types'
import { LearnpathElementType } from '@/api/types/LearnpathElementType'
import { APP_LOCALE, CONTENT_LOCALE } from '@/locale/constants'

import { createApiMethod, type } from './utils/createApiMethod'

export interface ManagedLearnpathSendEnabledNotificationsRequest {
  notification_types: Array<
    | 'content_edit'
    | 'learner_assignment'
    | 'completion_time_edit'
    | 'reactivation'
  >
}

export const ManagedLearnpathStatusSchema = v.picklist([
  'active',
  'scheduled',
  'draft',
  'inactive',
])

export const ManagedLearnpathTypeSchema = v.picklist([
  'mandatory',
  'linear',
  'certificate',
])

export const ManagedLearnpathsListQuerySchema = v.object({
  name: v.optional(v.string()),
  status: v.optional(ManagedLearnpathStatusSchema),
  types: v.optional(v.array(ManagedLearnpathTypeSchema)),
  user: v.optional(v.array(v.string())),
  group: v.optional(v.array(v.string())),
  owner: v.optional(v.array(v.nullable(v.string()))),
  lang: v.optional(v.picklist(CONTENT_LOCALE)),
  is_course: v.optional(v.boolean()),
  with_managed_learners: v.optional(v.boolean()),
  only_direct_learners_stats: v.optional(v.boolean()),
  categories: v.optional(v.array(v.string())),
})

export type ManagedLearnpathStatus = v.InferInput<
  typeof ManagedLearnpathStatusSchema
>
export type ManagedLearnpathType = v.InferInput<
  typeof ManagedLearnpathTypeSchema
>
export type ManagedLearnpathsListQuery = v.InferInput<
  typeof ManagedLearnpathsListQuerySchema
>

const ElementCreationQuerySchema = v.object({
  next_element_id: v.optional(v.nullable(v.string())),
})

type ElementCreationQuery = v.InferInput<typeof ElementCreationQuerySchema>

const CatalogPlatformTopicsQuerySchema = v.object({
  q: v.optional(v.nullable(v.string())),
  order: v.optional(
    v.nullable(
      v.picklist(['relevance', 'top_rated', 'most_viewed', 'most_recent']),
    ),
  ),
  tags: v.optional(v.array(v.string())),
  locale: v.optional(v.nullable(v.picklist(APP_LOCALE))),
  content_type: v.optional(
    v.nullable(v.picklist(['video', 'text', 'chatbot', 'scorm'])),
  ),
  offset: v.optional(v.nullable(v.number())),
  limit: v.optional(v.nullable(v.number())),
})

type CatalogPlatformTopicsQuery = v.InferInput<
  typeof CatalogPlatformTopicsQuerySchema
>

/**
 * Endpoint: /learnpaths/managed
 */
export const learnpathsManaged = {
  learnpath: {
    list: createApiMethod({
      method: 'GET',
      path: '/learnpaths/managed',
      query: type<ManagedLearnpathsListQuery>(),
      response: type<ManagedLearnpaths>(),
      config: { paramsSerializer: { indexes: null } },
    }),

    create: createApiMethod({
      method: 'POST',
      path: '/learnpaths/managed',
      data: type<ManagedLearnpathsPostRequest>(),
      response: type<ManagedLearnpath>(),
    }),

    details: createApiMethod({
      method: 'GET',
      path: '/learnpaths/managed/:learnpathId',
      query: type<{ only_direct_learners_stats?: boolean }>(),
      response: type<ManagedLearnpath>(),
    }),

    update: createApiMethod({
      method: 'PUT',
      path: '/learnpaths/managed/:learnpathId',
      data: type<ManagedLearnpathPutRequest>(),
      response: type<ManagedLearnpath>(),
    }),

    delete: createApiMethod({
      method: 'DELETE',
      path: '/learnpaths/managed/:learnpathId',
    }),
  },

  categories: {
    list: createApiMethod({
      method: 'GET',
      path: '/learnpaths/managed/categories',
      query: type<{ is_courses_view?: boolean }>(),
      response: type<LearnpathCategories>(),
    }),
  },

  actions: {
    activate: createApiMethod({
      method: 'POST',
      path: '/learnpaths/managed/:learnpathId/actions/activate',
    }),

    deactivate: createApiMethod({
      method: 'POST',
      path: '/learnpaths/managed/:learnpathId/actions/deactivate',
    }),

    duplicate: createApiMethod({
      method: 'POST',
      path: '/learnpaths/managed/:learnpathId/actions/duplicate',
      response: type<ManagedLearnpathDuplicateActionResponse>(),
    }),

    duplicateAsCourse: createApiMethod({
      method: 'POST',
      path: '/learnpaths/managed/:learnpathId/actions/duplicate-as-course',
      response: type<ManagedLearnpathDuplicateAsCourseActionResponse>(),
    }),

    duplicateAsLearnpath: createApiMethod({
      method: 'POST',
      path: '/learnpaths/managed/:learnpathId/actions/duplicate-as-learnpath',
      response: type<ManagedLearnpathDuplicateAsLearnpathActionResponse>(),
    }),

    toggleLinkSharing: createApiMethod({
      method: 'POST',
      path: '/learnpaths/managed/:learnpathId/actions/toggle-link-sharing',
      response: type<ManagedLearnpathSharingLink>(),
    }),

    sendExpirationReminderBulk: createApiMethod({
      method: 'POST',
      path: '/learnpaths/managed/:learnpathId/actions/send-expiration-reminder-bulk',
    }),
  },

  notifications: {
    sendEnabledNotifications: createApiMethod({
      method: 'POST',
      path: '/learnpaths/managed/:learnpathId/send-enabled-notifications',
      data: type<ManagedLearnpathSendEnabledNotificationsRequest>(),
    }),
  },

  elements: {
    list: createApiMethod({
      method: 'GET',
      path: '/learnpaths/managed/:learnpathId/elements',
      response: type<ManagedLearnpathElements>(),
    }),

    reorder: createApiMethod({
      method: 'PUT',
      path: '/learnpaths/managed/:learnpathId/elements',
      data: type<ManagedLearnpathElementsPutRequest>(),
      response: type<ManagedLearnpathElements>(),
    }),

    create: {
      embedded_video: createApiMethod({
        method: 'POST',
        path: '/learnpaths/managed/:learnpathId/elements/embedded-video',
        query: type<ElementCreationQuery>(),
        data: type<ManagedLearnpathEmbeddedVideoElementsPostRequest>(),
        response: type<ManagedLearnpathEmbeddedVideoElement>(),
      }),
      event: createApiMethod({
        method: 'POST',
        path: '/learnpaths/managed/:learnpathId/elements/event',
        query: type<ElementCreationQuery>(),
        data: type<ManagedLearnpathEventElementsPostRequest>(),
        response: type<ManagedLearnpathEventElement>(),
      }),
      file: createApiMethod({
        method: 'POST',
        path: '/learnpaths/managed/:learnpathId/elements/file',
        query: type<ElementCreationQuery>(),
        data: type<ManagedLearnpathFileElementsPostRequest>(),
        response: type<ManagedLearnpathFileElement>(),
      }),
      link: createApiMethod({
        method: 'POST',
        path: '/learnpaths/managed/:learnpathId/elements/link',
        query: type<ElementCreationQuery>(),
        data: type<ManagedLearnpathLinkElementsPostRequest>(),
        response: type<ManagedLearnpathLinkElement>(),
      }),
      platform_topic: createApiMethod({
        method: 'POST',
        path: '/learnpaths/managed/:learnpathId/elements/platform-topic',
        query: type<ElementCreationQuery>(),
        data: type<ManagedLearnpathPlatformTopicElementPostRequest[]>(),
        response: type<ManagedLearnpathPlatformTopicElementsPostResponse[]>(),
      }),
      quiz: createApiMethod({
        method: 'POST',
        path: '/learnpaths/managed/:learnpathId/elements/quiz',
        query: type<ElementCreationQuery>(),
        data: type<ManagedLearnpathQuizElementsPostRequest>(),
        response: type<ManagedLearnpathQuizElement>(),
      }),
      scorm: createApiMethod({
        method: 'POST',
        path: '/learnpaths/managed/:learnpathId/elements/scorm',
        query: type<ElementCreationQuery>(),
        data: type<ManagedLearnpathScormElementsPostRequest>(),
        response: type<ManagedLearnpathScormElement>(),
      }),
      text: createApiMethod({
        method: 'POST',
        path: '/learnpaths/managed/:learnpathId/elements/text',
        query: type<ElementCreationQuery>(),
        data: type<ManagedLearnpathTextElementsPostRequest>(),
        response: type<ManagedLearnpathTextElement>(),
      }),
      video: createApiMethod({
        method: 'POST',
        path: '/learnpaths/managed/:learnpathId/elements/video',
        query: type<ElementCreationQuery>(),
        data: type<ManagedLearnpathVideoElementsPostRequest>(),
        response: type<ManagedLearnpathVideoElement>(),
      }),
    } satisfies Record<LearnpathElementType, unknown>,

    details: {
      embedded_video: createApiMethod({
        method: 'GET',
        path: '/learnpaths/managed/:learnpathId/elements/embedded-video/:elementId',
        response: type<ManagedLearnpathEmbeddedVideoElement>(),
      }),
      event: createApiMethod({
        method: 'GET',
        path: '/learnpaths/managed/:learnpathId/elements/event/:elementId',
        response: type<ManagedLearnpathEventElement>(),
      }),
      file: createApiMethod({
        method: 'GET',
        path: '/learnpaths/managed/:learnpathId/elements/file/:elementId',
        response: type<ManagedLearnpathFileElement>(),
      }),
      link: createApiMethod({
        method: 'GET',
        path: '/learnpaths/managed/:learnpathId/elements/link/:elementId',
        response: type<ManagedLearnpathLinkElement>(),
      }),
      platform_topic: createApiMethod({
        method: 'GET',
        path: '/learnpaths/managed/:learnpathId/elements/platform-topic/:elementId',
        response: type<ManagedLearnpathPlatformTopicElement>(),
      }),
      quiz: createApiMethod({
        method: 'GET',
        path: '/learnpaths/managed/:learnpathId/elements/quiz/:elementId',
        response: type<ManagedLearnpathQuizElement>(),
      }),
      scorm: createApiMethod({
        method: 'GET',
        path: '/learnpaths/managed/:learnpathId/elements/scorm/:elementId',
        response: type<ManagedLearnpathScormElement>(),
      }),
      text: createApiMethod({
        method: 'GET',
        path: '/learnpaths/managed/:learnpathId/elements/text/:elementId',
        response: type<ManagedLearnpathTextElement>(),
      }),
      video: createApiMethod({
        method: 'GET',
        path: '/learnpaths/managed/:learnpathId/elements/video/:elementId',
        response: type<ManagedLearnpathVideoElement>(),
      }),
    } satisfies Record<LearnpathElementType, unknown>,

    update: {
      embedded_video: createApiMethod({
        method: 'PUT',
        path: '/learnpaths/managed/:learnpathId/elements/embedded-video/:elementId',
        data: type<ManagedLearnpathEmbeddedVideoElementPutRequest>(),
        response: type<ManagedLearnpathEmbeddedVideoElement>(),
      }),
      event: createApiMethod({
        method: 'PUT',
        path: '/learnpaths/managed/:learnpathId/elements/event/:elementId',
        data: type<ManagedLearnpathEventElementPutRequest>(),
        response: type<ManagedLearnpathEventElement>(),
      }),
      file: createApiMethod({
        method: 'PUT',
        path: '/learnpaths/managed/:learnpathId/elements/file/:elementId',
        data: type<ManagedLearnpathFileElementPutRequest>(),
        response: type<ManagedLearnpathFileElement>(),
      }),
      link: createApiMethod({
        method: 'PUT',
        path: '/learnpaths/managed/:learnpathId/elements/link/:elementId',
        data: type<ManagedLearnpathLinkElementPutRequest>(),
        response: type<ManagedLearnpathLinkElement>(),
      }),
      platform_topic: createApiMethod({
        method: 'PUT',
        path: '/learnpaths/managed/:learnpathId/elements/platform-topic/:elementId',
        data: type<ManagedLearnpathPlatformTopicElementPutRequest>(),
        response: type<ManagedLearnpathPlatformTopicElement>(),
      }),
      quiz: createApiMethod({
        method: 'PUT',
        path: '/learnpaths/managed/:learnpathId/elements/quiz/:elementId',
        data: type<ManagedLearnpathQuizElementPutRequest>(),
        response: type<ManagedLearnpathQuizElement>(),
      }),
      scorm: createApiMethod({
        method: 'PUT',
        path: '/learnpaths/managed/:learnpathId/elements/scorm/:elementId',
        data: type<ManagedLearnpathScormElementPutRequest>(),
        response: type<ManagedLearnpathScormElement>(),
      }),
      text: createApiMethod({
        method: 'PUT',
        path: '/learnpaths/managed/:learnpathId/elements/text/:elementId',
        data: type<ManagedLearnpathTextElementPutRequest>(),
        response: type<ManagedLearnpathTextElement>(),
      }),
      video: createApiMethod({
        method: 'PUT',
        path: '/learnpaths/managed/:learnpathId/elements/video/:elementId',
        data: type<ManagedLearnpathVideoElementPutRequest>(),
        response: type<ManagedLearnpathVideoElement>(),
      }),
    } satisfies Record<LearnpathElementType, unknown>,

    delete: {
      embedded_video: createApiMethod({
        method: 'DELETE',
        path: '/learnpaths/managed/:learnpathId/elements/embedded-video/:elementId',
      }),
      event: createApiMethod({
        method: 'DELETE',
        path: '/learnpaths/managed/:learnpathId/elements/event/:elementId',
      }),
      file: createApiMethod({
        method: 'DELETE',
        path: '/learnpaths/managed/:learnpathId/elements/file/:elementId',
      }),
      link: createApiMethod({
        method: 'DELETE',
        path: '/learnpaths/managed/:learnpathId/elements/link/:elementId',
      }),
      platform_topic: createApiMethod({
        method: 'DELETE',
        path: '/learnpaths/managed/:learnpathId/elements/platform-topic/:elementId',
      }),
      quiz: createApiMethod({
        method: 'DELETE',
        path: '/learnpaths/managed/:learnpathId/elements/quiz/:elementId',
      }),
      scorm: createApiMethod({
        method: 'DELETE',
        path: '/learnpaths/managed/:learnpathId/elements/scorm/:elementId',
      }),
      text: createApiMethod({
        method: 'DELETE',
        path: '/learnpaths/managed/:learnpathId/elements/text/:elementId',
      }),
      video: createApiMethod({
        method: 'DELETE',
        path: '/learnpaths/managed/:learnpathId/elements/video/:elementId',
      }),
    } satisfies Record<LearnpathElementType, unknown>,
  },

  members: {
    list: createApiMethod({
      method: 'GET',
      path: '/learnpaths/managed/:learnpathId/members',
      response: type<ManagedLearnpathMembers>(),
    }),
    create: createApiMethod({
      method: 'POST',
      path: '/learnpaths/managed/:learnpathId/members',
      data: type<ManagedLearnpathMembersPostRequest>(),
      response: type<ManagedLearnpathMembers>(),
    }),
    delete: createApiMethod({
      method: 'DELETE',
      path: '/learnpaths/managed/:learnpathId/members',
      data: type<ManagedLearnpathMembersDeleteRequest>(),
      response: type<ManagedLearnpathMembers>(),
    }),
  },

  userGroups: {
    list: createApiMethod({
      method: 'GET',
      path: '/learnpaths/managed/:learnpathId/user_groups',
      response: type<ManagedLearnpathUserGroups>(),
    }),
    create: createApiMethod({
      method: 'POST',
      path: '/learnpaths/managed/:learnpathId/user_groups',
      data: type<ManagedLearnpathUserGroupsPostRequest>(),
    }),
    update: createApiMethod({
      method: 'PUT',
      path: '/learnpaths/managed/:learnpathId/user_groups',
      data: type<ManagedLearnpathUserGroupsPutRequest>(),
    }),
    delete: createApiMethod({
      method: 'DELETE',
      path: '/learnpaths/managed/:learnpathId/user_groups',
      data: type<ManagedLearnpathUserGroupsDeleteRequest>(),
    }),
  },

  candidates: {
    list: createApiMethod({
      method: 'GET',
      path: '/learnpaths/managed/:learnpathId/candidates',
      query: type<{ query?: string | null }>(),
      response: type<ManagedLearnpathCandidates>(),
    }),
  },

  catalog: {
    platformTopics: {
      list: createApiMethod({
        method: 'GET',
        path: '/learnpaths/managed/:learnpathId/catalog/platform-topics',
        query: type<CatalogPlatformTopicsQuery>(),
        response: type<ManagedLearnpathPlatformTopicsCatalog>(),
        config: { paramsSerializer: { indexes: null } },
      }),
    },
  },

  stats: {
    completion: {
      details: createApiMethod({
        method: 'GET',
        path: '/learnpaths/managed/:learnpathId/stats/completion/csv',
        response: type<Blob>(),
        config: { responseType: 'blob' },
      }),
      create: createApiMethod({
        method: 'POST',
        path: '/learnpaths/managed/stats/completion/csv',
      }),
    },
    quiz: {
      details: createApiMethod({
        method: 'GET',
        path: '/learnpaths/managed/:learnpathId/stats/quiz/csv',
        response: type<Blob>(),
        config: { responseType: 'blob' },
      }),
    },
  },
}

export const learnpathsManagedQueries = {
  all: () => ['learnpaths-managed'] as const,

  learnpath: {
    list: (query: ManagedLearnpathsListQuery = {}) => ({
      // eslint-disable-next-line @tanstack/query/exhaustive-deps
      queryKey: [
        ...learnpathsManagedQueries.all(),
        'list',
        ...(isEmpty(query) ? [] : [query]),
      ] as const,
      queryFn: () => learnpathsManaged.learnpath.list({ query }),
    }),
    details: (
      learnpathId: string,
      query: { only_direct_learners_stats?: boolean } = {},
    ) => ({
      // eslint-disable-next-line @tanstack/query/exhaustive-deps
      queryKey: [
        ...learnpathsManagedQueries.all(),
        learnpathId,
        'details',
        ...(isEmpty(query) ? [] : [query]),
      ] as const,
      queryFn: () =>
        learnpathsManaged.learnpath.details({ params: { learnpathId }, query }),
    }),
  },

  elements: {
    list: (learnpathId: string) => ({
      queryKey: [
        ...learnpathsManagedQueries.all(),
        learnpathId,
        'elements-list',
      ] as const,
      queryFn: () =>
        learnpathsManaged.elements.list({ params: { learnpathId } }),
    }),
    details: (
      learnpathId: string,
      elementType: LearnpathElementType,
      elementId: string,
    ) => ({
      // eslint-disable-next-line @tanstack/query/exhaustive-deps
      queryKey: [
        ...learnpathsManagedQueries.all(),
        learnpathId,
        elementType,
        elementId,
      ] as const,
      queryFn: () =>
        learnpathsManaged.elements.details[elementType]({
          params: { learnpathId, elementId },
        }),
    }),
  },

  membersList: (learnpathId: string) => ({
    queryKey: [
      ...learnpathsManagedQueries.all(),
      learnpathId,
      'members-list',
    ] as const,
    queryFn: () => learnpathsManaged.members.list({ params: { learnpathId } }),
  }),

  userGroupsList: (learnpathId: string) => ({
    queryKey: [
      ...learnpathsManagedQueries.all(),
      learnpathId,
      'user-groups-list',
    ] as const,
    queryFn: () =>
      learnpathsManaged.userGroups.list({ params: { learnpathId } }),
  }),

  categories: (query: { is_courses_view?: boolean } = {}) => ({
    queryKey: [...learnpathsManagedQueries.all(), 'categories', query] as const,
    queryFn: () => learnpathsManaged.categories.list({ query }),
  }),

  uploadsAsset: (assetType: 'file' | 'video', assetId?: string) => ({
    // eslint-disable-next-line @tanstack/query/exhaustive-deps
    queryKey: [
      ...learnpathsManagedQueries.all(),
      'uploads',
      assetType,
      ...(assetId ? [assetId] : []),
    ] as const,
    queryFn: () =>
      assetType === 'video'
        ? Api.learnpaths.uploadsVideoGetByUploadId(assetId ?? '')
        : Api.learnpaths.uploadsFileGetByUploadId(assetId ?? ''),
  }),
}

/**
 * Typed lookup maps for element CRUD methods.
 * Indexing `learnpathsManaged.elements.*` with a `LearnpathElementType` union
 * produces a union of all method return types. These records collapse that to
 * a single return type so mutation hooks can infer TData without casting at
 * every call site.
 */
export const elementMethodByType = {
  create: learnpathsManaged.elements.create as Record<
    LearnpathElementType,
    (args: {
      params: { learnpathId: string }
      query?: unknown
      data: unknown
    }) => Promise<
      | ManagedLearnpathElementOneOf
      | ManagedLearnpathPlatformTopicElementsPostResponse[]
    >
  >,
  update: learnpathsManaged.elements.update as Record<
    LearnpathElementType,
    (args: {
      data: unknown
      params: { learnpathId: string; elementId: string }
    }) => Promise<ManagedLearnpathElementOneOf>
  >,
  delete: learnpathsManaged.elements.delete as Record<
    LearnpathElementType,
    (args: {
      params: { learnpathId: string; elementId: string }
    }) => Promise<void>
  >,
}
